'use client';

import type {
  AnalyticsSnippet,
  EventProperties,
  Options,
  UserTraits,
} from '@segment/analytics-next';
import type { ExtraContext } from '@segment/analytics-node/dist/types/app/types';

import { nextBackendClient } from '@/api/v2/api';
import { COUNTRY_COOKIE } from '@/app/consts/cookies';
import { getCookie } from '@/utils/cookie';
import isBrowser from '@/utils/isBrowser';

// TODO: Remove after OneTrust CM rollout
import { OneTrustGateHandler } from './oneTrustGateHandler';
// TODO: Remove after OneTrust CM rollout
import { isServerSideAllowedEvent } from './serverSideEventAllowList';
import type {
  BaseDataLayer,
  GA4PayloadType,
  PartialGA4ItemType,
  PayloadItems,
} from './types';
import { canUseClientSideAnalytics, getRedditCid } from './utils';

interface DataLayer extends BaseDataLayer {
  ecommerce_data?: PartialGA4ItemType | GA4PayloadType;
  eventPath?: string | undefined;
  eventAttributes?: PayloadItems;
}

declare global {
  interface Window {
    analytics?: AnalyticsSnippet;
    dataLayer?: DataLayer[];
    ouraAnalytics: OuraAnalytics;
  }
}

export class OuraAnalytics {
  private serverBasePath: string;
  private statsigStableId: string | null;
  // TODO: Remove after OneTrust CM rollout
  private gateHandler = new OneTrustGateHandler();

  constructor({
    serverBasePath = '/api/analytics',
    statsigStableId = getCookie('statsigStableId'),
  }: {
    serverBasePath?: string;
    statsigStableId?: string | null;
  } = {}) {
    this.serverBasePath = serverBasePath;
    this.statsigStableId = statsigStableId;
  }

  /**
   * Update the OneTrust gate enabled status and process any queued events
   *
   * Called by React components (ConsentManagerProvider) that have access to Statsig gate values.
   *
   * Why we need a queue:
   * - Analytics events can fire immediately on page load (e.g., page views, identify calls)
   * - Statsig gate evaluation happens asynchronously in React useEffect hooks
   * - Without queuing, events would fire before we know which consent manager is active
   * - The queue holds events until the gate value is known (true=OneTrust, false=legacy)
   * - Once gate is set, all queued events are replayed with the correct routing logic
   *
   * TODO: Remove after OneTrust CM rollout - Remove this entire method
   */
  setOneTrustGateEnabled(enabled: boolean) {
    const queuedEvents = this.gateHandler.setGateValue(enabled);

    // Process queued events once we know the gate value
    if (queuedEvents) {
      queuedEvents.forEach(({ method, args }) => {
        if (method === 'track') {
          void this.track(
            ...(args as [string, EventProperties?, ExtraContext?]),
          );
        } else if (method === 'page') {
          void this.page(
            ...(args as [string, EventProperties?, ExtraContext?]),
          );
        } else {
          // method === 'identify'
          void this.identify(...(args as [UserTraits | string, ExtraContext?]));
        }
      });
    }
  }

  async track(
    event: string,
    properties?: EventProperties,
    options?: ExtraContext,
  ) {
    // TODO: Remove after OneTrust CM rollout
    if (this.gateHandler.shouldQueue()) {
      this.gateHandler.queueEvent('track', [event, properties, options] as [
        string,
        EventProperties?,
        ExtraContext?,
      ]);
      return;
    }

    // TODO: Remove after OneTrust CM rollout
    // Check if this event should bypass OneTrust's client-only restriction
    const forceServerSide = isServerSideAllowedEvent(event);

    const [type, trackFn] = this.getAnalyticsFn(
      window.analytics.track,
      this.serverTrack,
      forceServerSide,
    );

    this.debugAnalyticsEvent(
      `Sending ${type} analytics track`,
      event,
      properties,
      options,
    );
    const enhancedProperties = this.enhanceProperties(properties);
    const enhancedContext = this.enhanceContext(options);
    await this.callAnalyticsFn('track', () =>
      trackFn(event, enhancedProperties, {
        ...options,
        context: enhancedContext,
      }),
    );
  }

  async page(
    name: string,
    properties?: EventProperties,
    context?: ExtraContext,
  ) {
    // TODO: Remove after OneTrust CM rollout
    if (this.gateHandler.shouldQueue()) {
      this.gateHandler.queueEvent('page', [name, properties, context] as [
        string,
        EventProperties?,
        ExtraContext?,
      ]);
      return;
    }

    const [type, pageFn] = this.getAnalyticsFn(
      window.analytics.page,
      this.serverPage,
    );
    this.debugAnalyticsEvent(
      `Sending ${type} analytics page`,
      name,
      properties,
      context,
    );
    const enhancedProperties = this.enhanceProperties({ ...properties, name });
    const enhancedContext = this.enhanceContext(context);
    await this.callAnalyticsFn('page', () =>
      pageFn(name, enhancedProperties, enhancedContext),
    );
  }

  async identify(traits: UserTraits | string, context?: ExtraContext) {
    // TODO: Remove after OneTrust CM rollout
    if (this.gateHandler.shouldQueue()) {
      this.gateHandler.queueEvent('identify', [traits, context] as [
        UserTraits | string,
        ExtraContext?,
      ]);
      return;
    }

    const [type, identifyFn] = this.getAnalyticsFn(
      window.analytics.identify,
      this.serverIdentify,
    );
    this.debugAnalyticsEvent(
      `Sending ${type} analytics identify`,
      traits,
      context,
    );
    const enhancedContext = this.enhanceContext(context);
    await this.callAnalyticsFn('identify', async () =>
      identifyFn(traits, enhancedContext),
    );
  }

  gtmEvent(event: DataLayer) {
    window.dataLayer?.push(event);
  }

  private getAnalyticsFn = <T, S>(
    clientFn: T,
    serverFn: S,
    forceServerSide = false, // TODO: Remove after OneTrust CM rollout
  ) => {
    // TODO: Remove after OneTrust CM rollout - pass false instead
    const canClientSideTrack = canUseClientSideAnalytics(
      this.gateHandler.getGateValue(),
    );

    // TODO: Remove after OneTrust CM rollout
    // If event is on allowlist, force server-side regardless of OneTrust
    if (forceServerSide && this.gateHandler.getGateValue()) {
      return ['server-side (allowlist)', serverFn] as const;
    }

    const type = canClientSideTrack ? 'client' : 'server';
    const fn = canClientSideTrack ? clientFn : serverFn;
    return [type, fn] as const;
  };

  private enhanceProperties = (properties: EventProperties | undefined) => {
    // Inject userCountry from cookie
    const userCountry = getCookie(COUNTRY_COOKIE);
    return this.withFacebookPixelCookies(
      this.withRedditProperties(
        this.withStatisgIds(
          this.withPageInfo({
            ...properties,
            ...(userCountry && { userCountry }),
          }),
        ),
      ),
    );
  };

  private enhanceContext = (
    context: ExtraContext | undefined,
  ): ExtraContext => {
    const locale = getCookie('NEXT_LOCALE');
    return {
      page: this.withPageInfo(),
      locale: locale ?? 'en',
      ...context,
    };
  };

  private callAnalyticsFn = async (
    name: string,
    fn: () => Promise<unknown>,
  ) => {
    /**
     * Need to wrap fn because it can be non-Promise-returning function on initial load
     * (Segment queues up events before it's initialized and in that case it does not
     * return a promise).
     */
    return Promise.resolve(fn()).catch((e) => {
      // TODO - do we want to log these errors to datadog?
      this.debugAnalyticsEvent(`Error sending ${name} event`, e);
    });
  };

  private serverTrack = (
    event: string,
    properties?: EventProperties,
    options?: Options,
  ) => {
    return nextBackendClient.url(this.serverBasePath + '/track').post({
      event,
      properties,
      context: options?.context,
    });
  };

  private serverIdentify = (
    traits: UserTraits | string,
    context?: ExtraContext,
  ) => {
    return nextBackendClient.url(this.serverBasePath + '/identify').post({
      traits,
      context,
    });
  };

  private serverPage = (
    name: string,
    properties: EventProperties,
    context?: ExtraContext,
  ) => {
    return nextBackendClient.url(this.serverBasePath + '/page').post({
      name,
      properties,
      context,
    });
  };

  /*
   * This is needed to associate segment events to a statsig user
   */
  private withStatisgIds = (properties?: EventProperties) => {
    if (!this.statsigStableId) return properties;
    return {
      ...properties,
      statsigCustomIDs: ['stableID', this.statsigStableId],
      statsigStableId: this.statsigStableId,
    };
  };

  private withFacebookPixelCookies = (properties?: EventProperties) => {
    const fbcCookie = getCookie('_fbc');
    const fbpCookie = getCookie('_fbp');
    return {
      ...properties,
      ...(fbcCookie && { fbc: fbcCookie }),
      ...(fbpCookie && { fbp: fbpCookie }),
    };
  };

  private withRedditProperties = (properties?: EventProperties) => {
    const redditUuidCookie = getCookie('_rdt_uuid');
    const redditCid = getRedditCid(document.location.href);
    return {
      ...properties,
      ...(redditUuidCookie && { rdt_uuid: redditUuidCookie }),
      ...(redditCid && { rdt_cid: redditCid }),
    };
  };

  private withPageInfo = (properties?: EventProperties) => {
    return {
      path: document.location.pathname,
      referrer: document.referrer,
      title: document.title,
      url: document.location.href,
      search: document.location.search,
      ...properties,
    };
  };

  private debugAnalyticsEvent(...args: unknown[]) {
    // eslint-disable-next-line no-process-env -- dev feature only
    if (process.env.DEBUG_ANALYTICS === 'true') {
      console.log(...args); // eslint-disable-line no-console
    }
  }
}

// initialize global analytics class in the browser
if (isBrowser()) {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  window.ouraAnalytics = window?.ouraAnalytics ?? new OuraAnalytics();
}
